import {
  Selection,
  SortDescriptor,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
} from "@heroui/react";
import { useCallback, useEffect, useMemo, useState } from "react";

import BottomContent from "./BottomContent";
import { Animal } from "./Columns";

import { AnimalStore } from "@/store/useDataStore.ts";
import { Column } from "@/types/types.ts";

export default function TableComponent({
  row,
  RenderCell,
  TopContent,
  columns,
  data,
  statuses,
  searchColumns,
  setViewProfile,
  INITIAL_VISIBLE_COLUMNS,
}: any) {
  const [filterValue, setFilterValue] = useState("");

  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set([]));
  const [visibleColumns, setVisibleColumns] = useState<Selection>(
    new Set(INITIAL_VISIBLE_COLUMNS),
  );
  const [statusFilter, setStatusFilter] = useState<Selection>("all");

  const [rowsPerPage, setRowsPerPage] = useState(row);
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "age",
    direction: "ascending",
  });

  const [page, setPage] = useState(1);

  const hasSearchFilter = Boolean(filterValue);
  const { setAnimalInfo, clearAnimalInfo } = AnimalStore();

  const headerColumns = useMemo(() => {
    if (visibleColumns === "all") return columns;

    return columns.filter((column: Column) =>
      Array.from(visibleColumns).includes(column.uid),
    );
  }, [columns, visibleColumns]);

  const filteredItems = useMemo(() => {
    if (!Array.isArray(data)) return [];

    let filteredData = [...data];

    if (hasSearchFilter) {
      // const searchableFields = [ 'name', 'sex', 'firstName', 'email', 'farmName' ]
      filteredData = filteredData.filter((item) =>
        searchColumns.some((field: any) =>
          item[field]?.toLowerCase().includes(filterValue.toLowerCase()),
        ),
      );
    }

    if (
      statusFilter !== "all" &&
      Array.from(statusFilter).length !== statuses.length
    ) {
      filteredData = filteredData.filter((item) =>
        Array.from(statusFilter).includes(item.animalStatus),
      );
    }

    return filteredData;
  }, [
    data,
    hasSearchFilter,
    statusFilter,
    statuses.length,
    searchColumns,
    filterValue,
  ]);

  const pages = Math.ceil(filteredItems.length / rowsPerPage);

  const items = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;

    return filteredItems.slice(start, end);
  }, [page, filteredItems, rowsPerPage]);

  const sortedItems = useMemo(() => {
    return [...items].sort((a: Animal, b: Animal) => {
      const first = a[sortDescriptor.column as keyof Animal] as number;
      const second = b[sortDescriptor.column as keyof Animal] as number;
      const cmp = first < second ? -1 : first > second ? 1 : 0;

      return sortDescriptor.direction === "descending" ? -cmp : cmp;
    });
  }, [sortDescriptor, items]);

  const onNextPage = useCallback(() => {
    if (page < pages) {
      setPage(page + 1);
    }
  }, [page, pages]);

  const onPreviousPage = useCallback(() => {
    if (page > 1) {
      setPage(page - 1);
    }
  }, [page]);

  const onSearchChange = useCallback((value?: string) => {
    if (value) {
      setFilterValue(value);
      setPage(1);
    } else {
      setFilterValue("");
    }
  }, []);

  const onClear = useCallback(() => {
    setFilterValue("");
    setPage(1);
  }, []);

  const matchedItems = useMemo(() => {
    if (selectedKeys instanceof Set) {
      const matchedItems = sortedItems.filter((item) =>
        selectedKeys.has(item._id.toString()),
      );

      // const matchedItems = sortedItems.filter((item) => {
      //   const id = item?._id?.$oid ?? item?._id;
      //
      //   return id && selectedKeys.has(id.toString());
      // });
      // const matchedItems = sortedItems.filter((item) => {
      //   const id = item?._id?.$oid ?? item?._id;
      //
      //   if (!id) {
      //     console.warn("No key found for item:", item);
      //
      //     return false;
      //   }
      //
      //   return selectedKeys.has(id.toString());
      // });

      return matchedItems[0];
    }
  }, [sortedItems, selectedKeys]);

  const topContent = useMemo(() => {
    return (
      <TopContent
        columns={columns}
        dataLength={data?.length ?? 0}
        filterValue={filterValue}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        setStatusFilter={setStatusFilter}
        setVisibleColumns={setVisibleColumns}
        statusFilter={statusFilter}
        visibleColumns={visibleColumns}
        onClear={onClear}
        onSearchChange={onSearchChange}
      />
    );
  }, [
    TopContent,
    columns,
    data?.length,
    filterValue,
    statusFilter,
    visibleColumns,
    onClear,
    onSearchChange,
  ]);

  useEffect(() => {
    if (matchedItems) {
      setAnimalInfo(matchedItems);
      // console.log("matchedItems updated");
    } else if (!matchedItems) {
      clearAnimalInfo();
    }
  }, [clearAnimalInfo, matchedItems, setAnimalInfo]);

  const bottomContent = useMemo(() => {
    return (
      <div className={"z-0"}>
        <BottomContent
          filteredItems={filteredItems}
          page={page}
          pages={pages}
          selectedKeys={selectedKeys}
          setPage={setPage}
          onNextPage={onNextPage}
          onPreviousPage={onPreviousPage}
        />
      </div>
    );
  }, [filteredItems, page, pages, selectedKeys, onNextPage, onPreviousPage]);

  return (
    <>
      {data && (
        <Table
          isCompact
          isHeaderSticky
          isStriped
          aria-label="Example table with custom cells, pagination and sorting"
          bottomContent={bottomContent}
          bottomContentPlacement="outside"
          classNames={{
            wrapper:
              "max-h-[782px] w-full shadow-xl" +
              "shadow-stone-300 dark:shadow-stone-700",
            th: "bg-[#015354ff] text-amber-200",
          }}
          color={"secondary"}
          selectedKeys={selectedKeys}
          selectionBehavior={"toggle"}
          selectionMode="multiple"
          sortDescriptor={sortDescriptor}
          topContent={topContent}
          topContentPlacement="outside"
          onSelectionChange={setSelectedKeys}
          onSortChange={setSortDescriptor}
        >
          <TableHeader columns={headerColumns}>
            {(column: Column) => (
              <TableColumn
                key={column.uid}
                align={column.uid === "actions" ? "center" : "start"}
                allowsSorting={column.sortable}
              >
                <Tooltip key={column.uid} content={column.description}>
                  <span>{column.name}</span>
                </Tooltip>
              </TableColumn>
            )}
          </TableHeader>
          <TableBody
            emptyContent={
              <div className="h-full overflow-hidden  mt-20 py-2  text-center flex flex-col justify-center items-center gap-2">
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No Data Found.
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by creating a Registering.
                </p>
              </div>
            }
            items={sortedItems}
          >
            {(item) => {
              return (
                <TableRow key={item._id}>
                  {(columnKey) => (
                    <TableCell>
                      <RenderCell
                        columnKey={columnKey}
                        data={item}
                        setViewProfile={setViewProfile}
                      />
                    </TableCell>
                  )}
                </TableRow>
              );
            }}
          </TableBody>
        </Table>
      )}
      {/*{drawerOpen && <Profile/>}*/}
    </>
  );
}
